import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  OnInit,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { ControlsOf, FormGroup } from '@ngneat/reactive-forms';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FormQuestionStandard } from '../../../core/models/form-group-mapping.model';
import { ReadonlyState } from '../../../core/models/form.model';
import { QuestionStructureSarskildaVillkor } from '../../../core/models/question/question-sarskillda-vilkor.model';
import { Question } from '../../../core/models/question/question.model';
import { RendererFormsQuery } from '../../../core/queries/renderer-forms.query';
import { QuestionValueChangeService } from '../../../core/services/question-value-change.service';

@UntilDestroy()
@Component({
  selector: 'lib-question-sarskilda-villkor',
  templateUrl: './sarskilda-villkor.component.html',
  styleUrl: './sarskilda-villkor.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SarskildaVillkorComponent implements OnInit {
  question = input<Question>();
  questionForm = input<FormGroup<ControlsOf<FormQuestionStandard>>>();

  activeLang: Signal<string>;
  comments: Signal<FormControl<string>[]>;
  readOnly: Signal<ReadonlyState>;
  commentsReadonly: WritableSignal<string>[] = [];

  get sarskildaVillkor() {
    return (this.question().structure as QuestionStructureSarskildaVillkor)
      .sarskildaVillkor;
  }

  get anvisningarRekommendationer() {
    return (this.question().structure as QuestionStructureSarskildaVillkor)
      .anvisningarRekommendationer;
  }

  constructor(
    private translocoService: TranslocoService,
    private questionValueChangeService: QuestionValueChangeService,
    private formsQuery: RendererFormsQuery,
  ) {
    this.activeLang = toSignal(this.translocoService.langChanges$);

    this.comments = computed(() => {
      const comments = [];
      const value = this.questionForm().get('value').value;
      const parsedValue = value ? JSON.parse(value) : {};

      for (const sarskiltVillkor of (
        this.question().structure as QuestionStructureSarskildaVillkor
      ).sarskildaVillkor) {
        comments.push(
          this.createCommentFormControl(
            sarskiltVillkor.id,
            parsedValue[sarskiltVillkor.id],
          ),
        );
        this.commentsReadonly.push(signal(parsedValue[sarskiltVillkor.id]));
      }
      return comments;
    });

    this.readOnly = toSignal(this.formsQuery.selectReadOnly());

    toObservable(this.readOnly)
      .pipe(untilDestroyed(this))
      .subscribe((isReadOnly) => {
        if (isReadOnly) {
          this.setReadonly();
        }
      });
  }

  ngOnInit() {
    this.questionValueChangeService.patchInitialValue(
      this.question().id,
      undefined,
      this.questionForm().get('value'),
      this.question(),
    );

    this.questionValueChangeService
      .valueChangeListener(
        this.questionForm().get('value'),
        {
          questionId: this.question().id,
        },
        this.question(),
      )
      .pipe(untilDestroyed(this))
      .subscribe();

    this.questionValueChangeService
      .patchValueListener(
        this.question(),
        undefined,
        this.questionForm().get('value'),
      )
      .pipe(untilDestroyed(this))
      .subscribe(() => this.setReadonly());

    if (this.readOnly()) {
      this.setReadonly();
    }
  }

  createCommentFormControl(id: string, value: string) {
    const fc = new FormControl<string>(value, Validators.maxLength(1500));
    fc.valueChanges.pipe(untilDestroyed(this)).subscribe((val) => {
      this.setValue(id, val);
    });
    return fc;
  }

  setValue(id: string, value: string) {
    const oldValue = this.questionForm().value.value;
    const parsedValue = oldValue ? JSON.parse(oldValue) : {};
    parsedValue[id] = value;
    this.questionForm().get('value').patchValue(JSON.stringify(parsedValue));
    this.questionForm().get('value').markAsDirty();
  }

  private setReadonly() {
    const comments = this.comments();
    for (
      let i = 0;
      i <
      (this.question().structure as QuestionStructureSarskildaVillkor)
        .sarskildaVillkor.length;
      i++
    ) {
      this.commentsReadonly[i].set(comments[i].value);
    }
  }
}
